import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CreditBalance {
  balance: number;
  credits_purchased_total: number;
  credits_spent_total: number;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  transaction_type: 'purchase' | 'match_request' | 'super_match' | 'breeding_match';
  credits_amount: number;
  balance_after: number;
  package_type: string | null;
  payment_reference: string | null;
  price_usd: number | null;
  created_at: string;
}

export const CREDIT_PACKAGES = {
  starter: { 
    credits: 10, 
    price: 14.99,  // Web price (PayPal)
    nativePrice: 21.99,  // Native price (Apple/Google)
    packageId: 'petmatch_10_credits',  // RevenueCat Package ID
    productId: 'io.petius.consumable.credits.10',  // Store Product ID
    label: 'Starter Pack' 
  },
  basic: { 
    credits: 25, 
    price: 32.99,  // Web price (PayPal)
    nativePrice: 47.99,  // Native price (Apple/Google)
    packageId: 'petmatch_25_credits',  // RevenueCat Package ID
    productId: 'io.petius.consumable.credits.25',  // Store Product ID
    label: 'Basic Pack' 
  },
  premium: { 
    credits: 50, 
    price: 49.99,  // Web price (PayPal)
    nativePrice: 71.99,  // Native price (Apple/Google)
    packageId: 'petmatch_50_credits',  // RevenueCat Package ID
    productId: 'io.petius.consumable.credits.50',  // Store Product ID
    label: 'Premium Pack' 
  },
  ultimate: { 
    credits: 100, 
    price: 89.99,  // Web price (PayPal)
    nativePrice: 128.99,  // Native price (Apple/Google)
    packageId: 'petmatch_100_credits',  // RevenueCat Package ID
    productId: 'io.petius.consumable.credits.100',  // Store Product ID
    label: 'Ultimate Pack' 
  }
} as const;

export const MATCH_COSTS = {
  playdate: 1,
  friendship: 1,
  adoption: 2,
  breeding: 4,
  super_match: 5
} as const;

export const useCredits = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Obtener balance de créditos
  const { data: balance, isLoading: loadingBalance, refetch: refetchBalance } = useQuery<CreditBalance>({
    queryKey: ['credits-balance'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { action: 'get_balance' }
      });

      if (error) throw error;
      return {
        balance: data.balance || 0,
        credits_purchased_total: data.credits_purchased_total || 0,
        credits_spent_total: data.credits_spent_total || 0
      };
    }
  });

  // Obtener historial de transacciones
  const { data: transactions, isLoading: loadingTransactions } = useQuery<CreditTransaction[]>({
    queryKey: ['credits-transactions'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { action: 'get_history' }
      });

      if (error) throw error;
      return data.transactions || [];
    }
  });

  // Comprar créditos
  const purchaseCredits = useMutation({
    mutationFn: async (params: {
      package_type: keyof typeof CREDIT_PACKAGES;
      payment_reference: string;
      price_usd?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: {
          action: 'purchase',
          ...params
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: '💰 Credits added!',
        description: `You received ${data.credits_added} credits. New balance: ${data.new_balance}`
      });
      queryClient.invalidateQueries({ queryKey: ['credits-balance'] });
      queryClient.invalidateQueries({ queryKey: ['credits-transactions'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Purchase failed',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Helper: Verificar si tiene créditos suficientes
  const hasEnoughCredits = (requiredCredits: number): boolean => {
    return (balance?.balance || 0) >= requiredCredits;
  };

  // Helper: Calcular costo de match
  const getMatchCost = (matchType: string, isSuperMatch: boolean = false): number => {
    if (isSuperMatch) return MATCH_COSTS.super_match;
    const normalizedType = matchType.toLowerCase() as keyof typeof MATCH_COSTS;
    return MATCH_COSTS[normalizedType] || 1;
  };

  return {
    // Data
    balance: balance?.balance || 0,
    creditsPurchased: balance?.credits_purchased_total || 0,
    creditsSpent: balance?.credits_spent_total || 0,
    transactions,
    
    // Actions
    purchaseCredits,
    refetchBalance,
    
    // Helpers
    hasEnoughCredits,
    getMatchCost,
    
    // Loading
    loadingBalance,
    loadingTransactions,
    
    // Constants
    packages: CREDIT_PACKAGES
  };
};
